import {
  default as React,
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState
} from "react";

type ConsentCategory = "essential" | "functional" | "analytics";

export interface CookieConsent {
  analytics: boolean;
  functional: boolean;
}

export const consentContext = createContext<CookieConsent | undefined>(
  undefined
);

export interface CookieDetail {
  name: string;
  category: ConsentCategory;
  type: "local" | "cookie" | "session";
  purpose: string;
  // Otherwise assumed to be document.domain
  domain?: string;
  if?: "ga" | "auth";
}

export const platformCompliance: CookieDetail = {
  type: "local",
  category: "functional",
  name: "platform",
  purpose:
    "Records your choice of platform for pages that display different videos based on your device type and operating system"
};
const lastAudienceSurveyAnsweredCompliance: CookieDetail = {
  type: "local",
  category: "essential",
  name: "lastAudienceSurveyAnswered",
  purpose:
    "Remembers the last ad hoc survey you viewed to avoid asking you more than once"
};
const browserInfoCompliance: CookieDetail = {
  type: "local",
  category: "functional",
  name: "browserInfo",
  purpose:
    "Stores country/region information to avoid refetching it unnecessarily"
};
const registrationPromptCompliance: CookieDetail = {
  type: "local",
  category: "essential",
  name: "playgroundSurveyPrompt",
  purpose:
    "Remembers whether you have interacted with the 'BBC micro:bit - the next gen' playground survey reminder notice to avoid showing it again"
};
export const sessionSettingsCompliance: CookieDetail = {
  type: "session",
  category: "essential",
  name: "sessionSettings",
  purpose:
    "Records which dialogs you've seen so we don't show them again during your session"
};

const config = {
  ga:
    process.env.GATSBY_STAGE === "PRODUCTION" ||
    process.env.GATSBY_STAGE === "STAGING"
      ? {}
      : undefined,
  custom: [
    platformCompliance,
    lastAudienceSurveyAnsweredCompliance,
    browserInfoCompliance,
    registrationPromptCompliance,
    sessionSettingsCompliance
  ]
};

const showConsent = (
  { userTriggered }: { userTriggered: boolean } = { userTriggered: false }
) => {
  const w = window as any;
  w.commonConsent?.show({ userTriggered, config });
};

const hideConsent = () => {
  const w = window as any;
  w.commonConsent?.hide();
};

export const manageCookies = () => showConsent({ userTriggered: true });

let cachedConsent: CookieConsent | undefined;

export const ConsentProvider = ({ children }: { children: ReactNode }) => {
  const [value, setValue] = useState<CookieConsent | undefined>(cachedConsent);
  useEffect(() => {
    const w = window as any;
    const updateListener = (event: CustomEvent<CookieConsent>) => {
      cachedConsent = event.detail;
      setValue(event.detail);
    };
    // Always listen for manual consent
    w.addEventListener("consentchange", updateListener);
    // If we already have a value then we don't need to trigger common consent
    if (!cachedConsent) {
      if (w.commonConsent) {
        showConsent();
      } else {
        w.addEventListener("consentinit", showConsent);
      }
    }
    return () => {
      w.removeEventListener("consentchange", updateListener);
      w.removeEventListener("consentinit", showConsent);
      hideConsent();
    };
  }, []);

  return (
    <consentContext.Provider value={value}>{children}</consentContext.Provider>
  );
};

export const useConsent = () => {
  return useContext(consentContext);
};
